import { mutationOptions, queryOptions } from '@tanstack/react-query';

import { client, HttpException, type components, type operations } from '@/libs/openapi';
import { articleQueries } from '@/resources/article/article.api';
import { mutationNamespace, queryNamespace } from '@/utils/query-namespace.util';

export const userQueries = queryNamespace('user', {
  me: queryOptions({
    queryKey: [],
    queryFn: ({ signal }) => client
      .GET('/api/v1/users/me', { signal })
      .then(({ data, error }) => {
        if (error) {
          throw new HttpException();
        }

        return data;
      }),
    staleTime: Infinity,
  }),
  interests: queryOptions({
    queryKey: [],
    queryFn: ({ signal }) => client
      .GET('/api/v1/users/interests', { signal })
      .then(({ data, error }) => {
        if (error) {
          throw new HttpException();
        }

        return data;
      }),
    staleTime: Infinity,
  }),
  specialties: queryOptions({
    queryKey: [],
    queryFn: ({ signal }) => client
      .GET('/api/v1/users/specialties', { signal })
      .then(({ data, error }) => {
        if (error) {
          throw new HttpException();
        }

        return data;
      }),
    staleTime: Infinity,
  }),
  dataRequestDetail: queryOptions({
    queryKey: [],
    queryFn: ({ signal }) => client
      .GET('/api/v1/users/data-request', { signal })
      .then(({ data, error }) => {
        if (error) {
          throw new HttpException();
        }

        return data;
      }),
    retry: false,
  }),
  confirmEmail: (data: components["schemas"]["EmailConfirmation"]) => queryOptions({
    queryKey: [data],
    queryFn: ({ signal }) => client
      .POST('/api/v1/users/confirm-email', { body: data, signal })
      .then(({ data, error, response }) => {
        if (error) {
          throw new HttpException({ message: typeof error.detail === 'string' ? error.detail : error.detail?.map(item => item.msg).join(', '), response });
        }

        return data;
      }),
  }),
  getPasswordStrength: (data: components["schemas"]["PasswordCheckRequest"]) => queryOptions({
    queryKey: [data],
    queryFn: ({ signal }) => client
      .POST('/api/v1/users/password-strength-check', { body: data, signal })
      .then(({ data, error, response }) => {
        if (error) {
          throw new HttpException({ message: error.detail?.map(item => item.msg).join(', '), response });
        }

        return data;
      }),
    enabled: false,
  }),
});

export const userMutations = mutationNamespace('user', {
  delete: mutationOptions({
    mutationFn: () => client
      .DELETE('/api/v1/users/me')
      .then(({ data, error }) => {
        if (error) {
          throw new HttpException();
        }

        return data;
      }),
    onSuccess: (_data, _variables, _onMutateResult, context) => context.client.clear(),
  }),
  requestData: mutationOptions<components["schemas"]["DataRequestDetail"]>({
    mutationFn: () => client
      .POST('/api/v1/users/data-request')
      .then(({ data, error }) => {
        if (error) {
          throw new HttpException();
        }

        return data;
      }),
  }),
  changePassword: mutationOptions<components["schemas"]["Token"], components['schemas']['HTTPValidationError'], components['schemas']['ChangePasswordRequest']>({
  mutationFn: (data: components['schemas']['ChangePasswordRequest']) => client
    .POST('/api/v1/users/change-password', { body: data })
    .then(({ data, error, response }) => {
      if (error) {
        throw new HttpException({
          isGlobal: response?.status !== 400, // The “Old password is incorrect” error is handled within the dialog
          message: typeof error.detail === 'string' ? error.detail : error.detail?.map(item => item.msg).join(', '),
          response,
        });
      }

      return data;
    }),
  }),
  register: mutationOptions({
    mutationFn: (data: components['schemas']['UserRegistration']) => client
      .POST('/api/v1/users/register', { body: data })
      .then(({ data, error, response }) => {
        if (error) {
          throw new HttpException({ message: typeof error.detail === 'string' ? error.detail : error.detail?.map(item => item.msg).join(', '), response });
        }

        return data;
      }),
  }),
  recoverPassword: mutationOptions({
    mutationFn: (data: operations["users-recover_password"]['parameters']['path']) => client
      .POST('/api/v1/users/password-recovery/{email}', { params: { path: { email: data.email } } })
      .then(({ data, error, response }) => {
        if (error) {
          throw new HttpException({ message: typeof error.detail === 'string' ? error.detail : error.detail?.map(item => item.msg).join(', '), response });
        }

        return data;
      }),
  }),
  resetPassword: mutationOptions({
    mutationFn: (data: components["schemas"]["NewPassword"]) => client
      .POST('/api/v1/users/reset-password', { body: data })
      .then(({ data, error, response }) => {
        if (error) {
          throw new HttpException({ message: typeof error.detail === 'string' ? error.detail : error.detail?.map(item => item.msg).join(', '), response });
        }

        return data;
      }),
  }),
  updateInterests: mutationOptions({
    mutationFn: (data: components["schemas"]["UserInterestRequest"]) => client
      .PUT('/api/v1/users/interests', { body: data })
      .then(({ data, error, response }) => {
        if (error) {
          throw new HttpException({ message: error.detail?.map(item => item.msg).join(', '), response });
        }

        return data;
      }),
    onSuccess: (_data, _variables, _onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: userQueries.interests.queryKey });
      context.client.invalidateQueries({ queryKey: articleQueries.all.queryKey });
    },
  }),
});
